import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, PageHeader } from 'antd';
import { generatePath, RouteComponentProps } from 'react-router';

import * as S from '../CulturesDetails/CulturesDetails.styles';
import { useCulturesSlice } from '../../slice';
import { RouteConstants } from 'app/routes';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { selectCreateCompanyLoading } from '../../slice/selectors';
import { selectCurrentCulturePolygonsIds } from '../../../Map/slice/selectors';
import MapCard from 'app/containers/Map/parts/MapCard/MapCard';
import { MapModeEnum } from 'app/containers/Map/slice/types';
import { LEFT_COL_WIDTH } from 'utils/constants';
import CreateCultureForm from '../../parts/CreateCultureForm/CreateCultureForm';
import { useMapSlice } from 'app/containers/Map/slice';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';

export const CREATE_CULTURE_FORM_ID = 'createCultureForm';

interface IProps extends RouteComponentProps {}

const CreateCulture = ({ history }: IProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { actions } = useCulturesSlice();

  const { actions: mapActions } = useMapSlice();

  const envId = useSelector(selectSelectedEnvId);
  const seasonId = useSelector(selectSelectedSeasonId);
  const companyId = useSelector(selectSelectedCompanyId);
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const createLoading = useSelector(selectCreateCompanyLoading);
  const culturePolygonsIds = useSelector(selectCurrentCulturePolygonsIds);

  const [selectedCultureId, setSelectedCultureId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (envId && seasonId) {
      if (selectedCultureId) {
        dispatch(
          actions.getCultureDetails({
            hiikoEnvironmentId: envId,
            cultureId: selectedCultureId,
            companyId: companyId ? Number(companyId) : undefined,
            seasonId,
          }),
        );
      } else {
        dispatch(
          actions.setCultureDetails({ companyId: null, culturesArr: [] }),
        );
      }

      // Load all polygons on map
      dispatch(
        mapActions.onLoad({
          envId,
          seasonId,
          companyId,
          companyGroupId,
        }),
      );
    }
  }, [
    mapActions,
    dispatch,
    envId,
    seasonId,
    companyId,
    companyGroupId,
    selectedCultureId,
    actions,
  ]);

  useEffect(() => {
    dispatch(mapActions.setSelectedPolygonsIds(culturePolygonsIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [culturePolygonsIds.length, mapActions, dispatch]);

  useEffect(() => {
    dispatch(actions.getCultures());
  }, [actions, dispatch]);

  if (!envId) {
    return null;
  }

  return (
    <>
      <PageHeader
        onBack={() =>
          history.push(
            generatePath(RouteConstants.cultures.list, {
              envId,
            }),
          )
        }
        className="page-header"
        title={t('Cultures.CreateCulture.Title', 'Add culture')}
        extra={[
          <Button
            key="submit"
            type="primary"
            form={CREATE_CULTURE_FORM_ID}
            htmlType="submit"
          >
            {t('Cultures.CreateCulture.Save', 'Save')}
          </Button>,
        ]}
      />
      {createLoading ? (
        <S.ContentWrapper>
          <S.Spinner tip="Loading..." size="large" />
        </S.ContentWrapper>
      ) : (
        <S.ContentWrapper>
          <S.LeftCol flex={LEFT_COL_WIDTH}>
            <CreateCultureForm setSelectedCultureId={setSelectedCultureId} />
          </S.LeftCol>

          <S.MapCol flex="auto">
            <MapCard mode={MapModeEnum.view} showLayerMenu showCulturesMenu />
          </S.MapCol>
        </S.ContentWrapper>
      )}
    </>
  );
};

export default CreateCulture;
